
import { useState, useMemo, useEffect } from "react";
import { Invoice, Filters, InvoiceStatus } from "@/types/payments";
import { getFilteredInvoicesByTab, applyFilters as applyFilterUtils, sortInvoices } from "@/utils/invoiceFilterUtils";

export function useAccountPayablesFiltering(invoices: Invoice[]) {
  // Local state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    status: 'all',
    vendor: '',
    minAmount: '',
    maxAmount: '',
    currency: 'all',
    dueDateFrom: '',
    dueDateTo: '',
    paymentDateFrom: '',
    paymentDateTo: ''
  });
  const [activeFilters, setActiveFilters] = useState<Filters>({
    status: 'all',
    vendor: '',
    minAmount: '',
    maxAmount: '',
    currency: 'all',
    dueDateFrom: '',
    dueDateTo: '',
    paymentDateFrom: '',
    paymentDateTo: ''
  });
  const [activeTab, setActiveTab] = useState("all");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Invoice | null;
    direction: 'asc' | 'desc' | 'none';
  }>({
    key: null,
    direction: 'none'
  });

  // Update filters based on active tab
  useEffect(() => {
    if (activeTab !== "all") {
      // Set the status filter based on the active tab
      const tabStatus = activeTab as InvoiceStatus;
      setFilters(prev => ({
        ...prev,
        status: tabStatus
      }));
      setActiveFilters(prev => ({
        ...prev,
        status: tabStatus
      }));
    } else {
      // Reset status filter to 'all' when switching to 'all' tab
      setFilters(prev => ({
        ...prev,
        status: 'all'
      }));
      setActiveFilters(prev => ({
        ...prev,
        status: 'all'
      }));
    }
  }, [activeTab]);

  // Handle status filter change with tab sync
  const handleStatusFilterChange = (status: InvoiceStatus | 'all') => {
    // If status is changed and different from the active tab, go to 'all' tab
    if (status !== activeTab && activeTab !== "all") {
      setActiveTab("all");
    }
    handleFilterChange('status', status);
  };

  // Get unique currencies for filter dropdown
  const uniqueCurrencies = useMemo(() => {
    return Array.from(new Set(invoices.map(invoice => invoice.currency)));
  }, [invoices]);

  // Handle filter changes (only updates the filter state, not applied yet)
  const handleFilterChange = (key: keyof Filters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Apply the current filter state to active filters
  const applyFiltersAction = () => {
    setActiveFilters({...filters});
  };

  // Reset all filters
  const resetFilters = () => {
    const resetState: Filters = {
      status: activeTab !== "all" ? activeTab as InvoiceStatus : 'all',
      vendor: '',
      minAmount: '',
      maxAmount: '',
      currency: 'all',
      dueDateFrom: '',
      dueDateTo: '',
      paymentDateFrom: '',
      paymentDateTo: ''
    };
    setFilters(resetState);
    setActiveFilters(resetState);
  };

  // Filter invoices based on current configuration
  const filteredInvoices = useMemo(() => {
    const tabFiltered = getFilteredInvoicesByTab(invoices, activeTab);
    return applyFilterUtils(tabFiltered, activeFilters);
  }, [invoices, activeFilters, activeTab]);

  // Sort invoices based on current sort configuration
  const sortedInvoices = useMemo(() => {
    if (sortConfig.key) {
      return sortInvoices(filteredInvoices, sortConfig);
    }
    return filteredInvoices;
  }, [filteredInvoices, sortConfig]);

  return {
    activeTab,
    setActiveTab,
    filters,
    activeFilters,
    handleFilterChange,
    handleStatusFilterChange,
    resetFilters,
    applyFilters: applyFiltersAction,
    showFilters,
    setShowFilters,
    uniqueCurrencies,
    filteredInvoices,
    sortedInvoices,
    sortConfig,
    setSortConfig
  };
}
